import * as React from 'react';
import { Link } from 'react-router';
import styled, { css } from 'styled-components';
import { rem } from 'polished';
import { __ } from '@bart.sk-eshop/react-i18n/lib';
import AnimateHeight from 'react-animate-height';

import {
  AccountIcon,
  ChevronDownIcon,
  XIcon,
} from '../../components/_layout/Header/HeaderNew/icons';
import { MenuNode } from '../../components/_layout/Header/HeaderNew/menuData';
import { URL_REGISTRATION_HUB } from '../../constants';

interface LoginMobileProps {
  loginUrl: string;
  defaultEmail: string;
  error?: string;
  newPasswordRequest?: boolean;
  menuTree?: MenuNode[] | null;
  onClose: () => void;
}

function LoginMobile({
  loginUrl,
  defaultEmail,
  error,
  newPasswordRequest,
  onClose,
}: LoginMobileProps) {
  const [open, setOpen] = React.useState<boolean>(
    !!error || !!newPasswordRequest,
  );
  const [showPassword, setShowPassword] = React.useState(false);

  React.useEffect(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }
    if (window.matchMedia('(min-width: 992px)').matches) {
      return;
    }
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.body.classList.add('hide-smartsupp');
    const smartsupp = (window as any).smartsupp;
    if (typeof smartsupp === 'function') {
      smartsupp('chat:hide');
    }
    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
      document.body.classList.remove('hide-smartsupp');
      if (typeof (window as any).smartsupp === 'function') {
        (window as any).smartsupp('chat:show');
      }
    };
  }, []);

  return (
    <Overlay>
      <Header>
        <HeaderLeft>
          <AccountIcon className="acc-ico" />
          <Title>{__('Môj účet')}</Title>
        </HeaderLeft>
        <CloseButton type="button" onClick={onClose} aria-label={__('Zavrieť')}>
          <XIcon />
        </CloseButton>
      </Header>

      <Body>
        <Tiles>
          <AccordionCard className={open ? 'open' : ''}>
            <Toggle
              type="button"
              className={open ? 'open' : ''}
              onClick={() => setOpen((o) => !o)}
              aria-expanded={open}
            >
              <span>{__('Prihlásenie')}</span>
              <ChevronDownIcon className="chev" />
            </Toggle>
            <AnimateHeight height={open ? 'auto' : 0} duration={200}>
              <FormCard>
                {error ? <Message className="error">{error}</Message> : null}
                {newPasswordRequest ? (
                  <Message className="info">
                    {__(
                      'Váš účet vyžaduje obnovu hesla. Do vašej emailovej schránky sme zaslali link na obnovu hesla.',
                    )}
                  </Message>
                ) : null}
                <form action={loginUrl} method="POST">
                  <Field
                    name="username"
                    defaultValue={defaultEmail}
                    placeholder={`${__('E-mail')} / ${__('IČO')}`}
                    autoComplete="username"
                  />
                  <PasswordWrap>
                    <Field
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      placeholder={__('Heslo')}
                      autoComplete="current-password"
                    />
                    <EyeButton
                      type="button"
                      onClick={() => setShowPassword((s) => !s)}
                      aria-label={__('Zobraziť heslo')}
                    >
                      {showPassword ? <EyeOffSvg /> : <EyeSvg />}
                    </EyeButton>
                  </PasswordWrap>
                  <CheckboxRow>
                    <input
                      type="checkbox"
                      name="remember_me"
                      value="1"
                      id="remember_me_mobile"
                    />
                    <label htmlFor="remember_me_mobile">
                      {__('Zapamätať prihlásenie')}
                    </label>
                  </CheckboxRow>
                  <Submit type="submit">{__('Prihlásiť sa')}</Submit>
                  <ForgotLink to="/zabudnute-heslo" onClick={onClose}>
                    {__('Zabudli ste heslo?')}
                  </ForgotLink>
                </form>
              </FormCard>
            </AnimateHeight>
          </AccordionCard>

          <RegistrationTile to={URL_REGISTRATION_HUB} onClick={onClose}>
            <span>{__('Registrácia')}</span>
          </RegistrationTile>
        </Tiles>
      </Body>
    </Overlay>
  );
}

const EyeSvg = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path
      d="M2.5 12S5.5 5.5 12 5.5 21.5 12 21.5 12 18.5 18.5 12 18.5 2.5 12 2.5 12Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const EyeOffSvg = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path
      d="M4 4l16 16M9.9 5.2A9.6 9.6 0 0 1 12 5c6.5 0 9.5 7 9.5 7a16 16 0 0 1-2.8 3.6M6.3 7.5A15.6 15.6 0 0 0 2.5 12s3 6.5 9.5 6.5a9 9 0 0 0 3-.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1300;
  background: ${({ theme }) => theme.color.white};
  display: flex;
  flex-direction: column;
  /* Mobile-only screen; never shown on desktop where the two-column layout is. */
  ${({ theme }) => theme.media('lg')} {
    display: none;
  }
`;

const Header = styled.div`
  flex: 0 0 auto;
  background: ${({ theme }) => theme.color.primary};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${rem(16)};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  color: ${({ theme }) => theme.color.white};
  .acc-ico {
    width: ${rem(32)};
    height: ${rem(32)};
  }
`;

const Title = styled.span`
  color: ${({ theme }) => theme.color.white};
  font-size: ${rem(16)};
  font-weight: 700;
  line-height: 1;
`;

const CloseButton = styled.button`
  appearance: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem(36)};
  height: ${rem(36)};
  border-radius: 50%;
  background: #bd1933;
  color: ${({ theme }) => theme.color.white};
  svg {
    width: ${rem(22)};
    height: ${rem(22)};
  }
`;

const Body = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: ${({ theme }) => theme.color.white};
`;

const Tiles = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  padding: ${rem(24)} ${rem(16)};
`;

const tileBase = css`
  appearance: none;
  border: none;
  cursor: pointer;
  width: 100%;
  font-family: inherit;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(16)};
  background: ${({ theme }) => theme.color.secondary};
  color: #171717;
  font-size: ${rem(15)};
  font-weight: 600;
  line-height: 1.6;
  border-radius: ${rem(8)};
  padding: ${rem(16)} ${rem(24)};
  text-decoration: none;
  .chev {
    width: ${rem(18)};
    height: ${rem(18)};
    flex: 0 0 auto;
    color: #171717;
  }
  &:hover {
    text-decoration: none;
    color: #171717;
  }
`;

const AccordionCard = styled.div`
  border-radius: ${rem(8)};
  &.open {
    background: ${({ theme }) => theme.color.white};
    box-shadow: 0 ${rem(2)} ${rem(10)} 0 rgba(0, 0, 0, 0.06);
    overflow: hidden;
  }
`;

const Toggle = styled.button`
  ${tileBase};
  position: relative;
  .chev {
    transition: transform 0.2s ease;
  }
  &.open {
    background: transparent;
    color: ${({ theme }) => theme.color.primary};
    border-radius: ${rem(8)} ${rem(8)} 0 0;
    .chev {
      transform: rotate(180deg);
      color: ${({ theme }) => theme.color.primary};
    }
    &::after {
      content: '';
      position: absolute;
      left: ${rem(16)};
      right: ${rem(16)};
      bottom: 0;
      height: ${rem(2)};
      background: ${({ theme }) => theme.color.secondary};
    }
  }
`;

const FormCard = styled.div`
  background: ${({ theme }) => theme.color.white};
  padding: ${rem(20)} ${rem(20)} ${rem(24)};
`;

const Message = styled.div`
  font-size: ${rem(14)};
  border-radius: ${rem(8)};
  padding: ${rem(10)} ${rem(12)};
  margin-bottom: ${rem(12)};
  &.error {
    background: #fdecef;
    color: ${({ theme }) => theme.color.primary};
  }
  &.info {
    background: #fff7d6;
    color: #6b5a00;
  }
`;

const Field = styled.input`
  display: block;
  width: 100%;
  box-sizing: border-box;
  font-family: inherit;
  font-size: ${rem(15)};
  color: #171717;
  background: ${({ theme }) => theme.color.white};
  border: ${rem(1)} solid #d9d9d9;
  border-radius: ${rem(8)};
  padding: ${rem(13)} ${rem(14)};
  margin-bottom: ${rem(10)};
  &::placeholder {
    color: #9b9b9b;
  }
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.color.primary};
  }
`;

const PasswordWrap = styled.div`
  position: relative;
  ${Field} {
    padding-right: ${rem(48)};
  }
`;

const EyeButton = styled.button`
  appearance: none;
  border: none;
  background: none;
  cursor: pointer;
  position: absolute;
  top: ${rem(7)};
  right: ${rem(8)};
  width: ${rem(34)};
  height: ${rem(34)};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9b9b9b;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  font-size: ${rem(14)};
  color: #171717;
  margin: ${rem(4)} 0 ${rem(18)};
  cursor: pointer;
  input {
    width: ${rem(18)};
    height: ${rem(18)};
    accent-color: ${({ theme }) => theme.color.primary};
  }
`;

const Submit = styled.button`
  appearance: none;
  border: none;
  cursor: pointer;
  width: 100%;
  font-family: inherit;
  background: ${({ theme }) => theme.color.primary};
  color: ${({ theme }) => theme.color.white};
  font-size: ${rem(16)};
  font-weight: 700;
  border-radius: ${rem(8)};
  padding: ${rem(14)};
`;

const ForgotLink = styled(Link)`
  display: block;
  text-align: center;
  font-size: ${rem(14)};
  color: ${({ theme }) => theme.color.primary};
  margin-top: ${rem(16)};
  text-decoration: none;
`;

const RegistrationTile = styled(Link)`
  ${tileBase};
`;

export default LoginMobile;
